import React, { useMemo } from 'react';

import type { SerializedStyles } from '@emotion/react';
import type { SelectOption } from '@rtkwlf/fenrir-react';
import { Combobox, HStack, Text } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';

import type { ItemType } from './SelectElements';
import { Wrap } from './SelectElements';

export interface DropdownSelectProps {
  /** List of items to be displayed as a Dropdown Select Input */
  items: Array<ItemType>;

  /** Pass the selected Item */
  selectedItem?: ItemType | null;

  /** Optional Title for the Select input */
  title?: string;

  /** Optional placeholder text */
  placeholder?: string;

  /** Optional placeholder text localize key */
  placeholderKey?: string;

  /** EmotionJS based css style */
  style?: SerializedStyles;

  /** callback function, called when user selects an item */
  handleSelectedItemChange?: (option: SelectOption | null) => void;
}

export const DropdownSelect: React.FC<DropdownSelectProps> = ({
  items = [],
  title,
  style,
  placeholder,
  placeholderKey,
  handleSelectedItemChange,
  selectedItem,
}) => {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      items.map(({ value, label }) => ({
        text: label ?? '',
        value,
      })),
    [items],
  );

  return (
    <Wrap css={style}>
      {title ? (
        <Text styledAs="body4" textTransform="capitalize">
          {title}
        </Text>
      ) : null}

      <HStack asChild xAlign="between">
        <Combobox
          fontFamily="body"
          options={options}
          filter="default"
          value={selectedItem?.label ?? ''}
          placeholder={
            placeholder ??
            (placeholderKey ? t(placeholderKey) : t('ui:dropDown.select'))
          }
          icon="caret"
          onSelection={handleSelectedItemChange}
          delay={450}
          isCreatable={false}
        />
      </HStack>
    </Wrap>
  );
};
